"use client";

// components/tracking/UtmCapture.tsx
// Client component that ensures every visitor gets an ff_sid session cookie.
// UTM parameters and referrer are captured when present, but session
// initialization fires unconditionally — direct/organic visitors need
// attribution coverage too. Renders nothing — zero visual footprint.
//
// Performance contract (Lighthouse #408/409/410/412/414/416, 2026-04-28):
// - Defer the work to requestIdleCallback so it never competes with FCP/LCP.
// - Short-circuit when (a) `ff_sid` is already set AND (b) no UTM params are
//   present — that's the return-visitor case where the session is already
//   stitched and there's nothing new to record. Saves a server-action POST
//   on every navigation, which is the single biggest TBT contributor on the
//   public site after GTM (which is already deferred via #294).

import { useEffect, useRef } from "react";
import { captureSession } from "@/lib/actions/tracking";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

function hasFfSidCookie(): boolean {
  if (typeof document === "undefined") return false;
  // Cheap O(n) string scan — no regex compilation per call.
  return document.cookie.includes("ff_sid=");
}

function scheduleIdle(fn: () => void): void {
  // requestIdleCallback in browsers that support it; setTimeout(0) fallback
  // for Safari (which still ships without rIC as of 2026). 1s deadline so
  // the work eventually fires even on a busy main thread.
  type IdleWindow = typeof window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(fn, { timeout: 1000 });
  } else {
    setTimeout(fn, 0);
  }
}

export default function UtmCapture() {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    scheduleIdle(() => {
      const params = new URLSearchParams(window.location.search);
      const hasUtm = UTM_KEYS.some((k) => params.has(k));

      // Return-visitor short-circuit: cookie already set + no UTM signal to
      // record means there is genuinely nothing new for the server to write.
      // Skip the POST entirely. First-touch / direct visitors (no cookie)
      // still fire so attribution coverage is unchanged for them.
      if (hasFfSidCookie() && !hasUtm) {
        return;
      }

      const utmSource = params.get("utm_source");
      const utmMedium = params.get("utm_medium");
      const utmCampaign = params.get("utm_campaign");
      const utmTerm = params.get("utm_term");
      const utmContent = params.get("utm_content");

      const referrer = document.referrer || undefined;

      captureSession({
        utmSource: utmSource || undefined,
        utmMedium: utmMedium || undefined,
        utmCampaign: utmCampaign || undefined,
        utmTerm: utmTerm || undefined,
        utmContent: utmContent || undefined,
        landingPage: window.location.pathname,
        referrer,
      });
    });
  }, []);

  return null;
}
